import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Button,
  CardActions,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarsIcon from "@mui/icons-material/Stars";

const ProductCard = ({ product }) => {

  return (
    <Card
      elevation={3}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "visible",
        position: "relative",
        transition: "transform 0.3s, box-shadow 0.3s",
        border: product.highlight
          ? `1px solid ${product.iconBgColor}30`
          : "none",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: "0 12px 20px rgba(0,0,0,0.1)",
        },
      }}
    >
      {product.highlight && (
        <Box
          sx={{
            position: "absolute",
            top: 15,
            right: 15,
            zIndex: 2,
          }}
        >
          <Tooltip title="Featured Solution" placement="top">
            <StarsIcon sx={{ color: product.iconBgColor, fontSize: 24 }} />
          </Tooltip>
        </Box>
      )}

      {/* Product Icon */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          left: 20,
          width: 68,
          height: 68,
          borderRadius: "50%",
          backgroundColor: "white",
          border: "2px solid",
          borderColor: product.iconBgColor || "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 15px rgba(0,0,0,0.12)",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            color: product.iconBgColor || "primary.main",
            fontSize: 32,
            animation: product.highlight ? "pulse 2s infinite" : "none",
            "@keyframes pulse": {
              "0%": {
                transform: "scale(1)",
              },
              "50%": {
                transform: "scale(1.1)",
              },
              "100%": {
                transform: "scale(1)",
              },
            },
          }}
        >
          {product.icon}
        </Box>
      </Box>

      {/* Product Title and Description */}
      <CardContent sx={{ pt: 6, pb: 2, flexGrow: 1 }}>
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            color: product.highlight ? product.iconBgColor : "text.primary",
          }}
        >
          {product.title}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          paragraph
          sx={{ mb: 3 }}
        >
          {product.description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Key Features */}
        <Box
          sx={{ bgcolor: "background.paper", borderRadius: 2, p: 1.5, mb: 2 }}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            sx={{
              mb: 1,
              color: "text.primary",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="span"
              sx={{
                bgcolor: product.iconBgColor || "primary.main",
                width: 4,
                height: 16,
                mr: 1,
                borderRadius: 4,
              }}
            ></Box>
            Key Features
          </Typography>
          <List dense disablePadding>
            {product.features.map((feature, index) => (
              <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleOutlineIcon
                    sx={{
                      color: product.iconBgColor || "primary.main",
                      fontSize: 18,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  primaryTypographyProps={{
                    variant: "body2",
                    color: "text.primary",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Tags/Technologies */}
        {product.tags && product.tags.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {product.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{
                  mr: 0.7,
                  mb: 0.7,
                  bgcolor: `${product.iconBgColor}10` || "primary.light",
                  color: product.iconBgColor || "primary.main",
                  fontWeight: 500,
                  borderRadius: "4px",
                }}
              />
            ))}
          </Box>
        )}
      </CardContent>

      {/* Card Actions */}
      <CardActions
        sx={{
          justifyContent: "flex-end",
          p: 2,
          pt: 0,
          borderTop: "1px solid",
          borderColor: "divider",
          mt: "auto",
        }}
      >
        <Button
          component={RouterLink}
          to="/contact#contact-section"
          endIcon={<ArrowForwardIcon />}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            px: 3,
            py: 0.7,
            bgcolor: product.iconBgColor || "primary.main",
            "&:hover": {
              bgcolor: product.iconBgColor
                ? `${product.iconBgColor}CC`
                : "primary.dark",
            },
          }}
        >
          Learn More
        </Button>
      </CardActions>
    </Card>
  );
};

export default ProductCard;
